import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lead } from '../../../generated/api/models/lead';
import type { LeadContextResponse } from '../../../generated/api/models/leadContextResponse';
import type { LeadListResponse } from '../../../generated/api/models/leadListResponse';
import type { LeadStatusCountsResponse } from '../../../generated/api/models/leadStatusCountsResponse';
import type { LeadBulkStatusResponse } from '../../../generated/api/models/leadBulkStatusResponse';
import type { LeadBulkAssignOwnerResponse } from '../../../generated/api/models/leadBulkAssignOwnerResponse';
import type { LeadFilterFacets } from '../../../generated/api/models/leadFilterFacets';
import { getLeadFilterFacetsApiV1PipelineLeadsFilterFacetsGet } from '../../../generated/api/pipeline/pipeline';
import axiosInstance from '../../../shared/api/axiosInstance';
import type { CriterionCompletionUpdate, Deal } from '../types';
import type { LeadStatusDetail } from '../utils/leadStatusDetail';
import { dealKeys } from './useDeals';
import { useLastUsedPipeline } from './useLastUsedPipeline';

// The leads list row is single-sourced from `shared/schemas/pipeline/lead.schema.json`
// via the generated client — a hand-written copy used to declare a `company_name`
// the API never sent, so the list's Company column rendered `-` for every lead.
export type { Lead };

export interface LeadCreateData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  organization_id?: string;
  company_name?: string;
  source?: string;
  owner_id?: string;
  lead_status?: string;
  contact_relationship?: string[];
  /**
   * The tenant's `contact` custom-field answers, as `LeadCreateRequest` takes
   * them. The route validates `partial=False`, so the quick-create dialog sends
   * the REQUIRED subset it collected — omitting a key is not clearing it.
   */
  custom_fields?: Record<string, unknown>;
}

export interface LeadCreateDealData {
  name: string;
  /**
   * Optional: the backend falls back to the pipeline the lead was routed to,
   * then the tenant default (lead-attribution design §D). An explicit value
   * always wins, so the dialog keeps sending the one the user sees.
   */
  pipeline_id?: string;
  company_id?: string;
  stage_id?: string;
  amount?: number;
  currency_code?: string;
  expected_close_date?: string;
  /**
   * The tenant's `pipeline_deal` custom-field answers, as `POST /pipeline/deals`
   * takes them. The route validates `partial=False` like its sibling, resolving
   * the contact's carried answers and tenant defaults underneath what is sent
   * here — so the dialog sends the REQUIRED subset it collected, and omitting a
   * key is not the same as clearing it.
   */
  custom_fields?: Record<string, unknown>;
}

/**
 * The narrowing inputs every lead read shares.
 *
 * ONE type for the list, the board's column badges and both bulk mutations,
 * because the backend resolves all four through one predicate helper: a second
 * hand-copied spelling here is how an escalated mutation comes to act on a wider
 * set than the table showed (ui-list-pages.md §11).
 */
export interface LeadListFilterParams {
  search?: string;
  lead_status?: string;
  /**
   * The rung's SECOND layer — which kind of engagement, or why the lead is out of
   * play. A closed vocabulary (`shared/config/lead_status_details.config.json`),
   * unlike `lead_status`, which is tenant data.
   */
  lead_status_detail?: LeadStatusDetail;
  source?: string;
  owner_id?: string;
  organization_id?: string;
  /** ISO instants. The window the list is showing, over `created_at`. */
  created_from?: string;
  created_to?: string;
}

interface LeadFilters extends LeadListFilterParams {
  /** Resolved server-side through SORTABLE_LEAD_COLUMNS — a page can't sort itself. */
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  skip?: number;
  limit?: number;
}

/**
 * A bulk lead mutation's reach: the ids the browser loaded, or the filter set
 * the surface is showing. The escalated arm names the FILTERS, never ids — the
 * browser cannot enumerate rows it never fetched.
 */
export type LeadBulkScope =
  | { ids: string[] }
  | { matchAllFiltered: true; filters: LeadListFilterParams };

function bulkScopeBody(scope: LeadBulkScope): Record<string, unknown> {
  return 'ids' in scope
    ? { ids: scope.ids }
    : { match_all_filtered: true, filters: scope.filters };
}

const BASE_URL = '/api/v1/pipeline';

export const leadKeys = {
  all: ['leads'] as const,
  list: (params?: LeadFilters) => [...leadKeys.all, 'list', params] as const,
  context: (contactId: string) => [...leadKeys.all, 'context', contactId] as const,
  statusCounts: (params?: LeadListFilterParams) =>
    [...leadKeys.all, 'status-counts', params] as const,
  filterFacets: (activeCompanyId?: string | null) =>
    [...leadKeys.all, 'filter-facets', activeCompanyId] as const,
};

export function useLeadFilterFacets(activeCompanyId?: string | null) {
  return useQuery({
    queryKey: leadKeys.filterFacets(activeCompanyId),
    queryFn: ({ signal }) => getLeadFilterFacetsApiV1PipelineLeadsFilterFacetsGet(signal),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Clean undefined values from params for consistent query key comparison.
 */
function cleanParams(params?: LeadFilters): LeadFilters | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as LeadFilters;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

// API functions
const leadsApi = {
  list: async (params?: LeadFilters): Promise<LeadListResponse> => {
    const response = await axiosInstance.get<LeadListResponse>(`${BASE_URL}/leads`, { params });
    return response.data;
  },
  create: async (data: LeadCreateData) => {
    const response = await axiosInstance.post(`${BASE_URL}/leads`, data);
    return response.data.data;
  },
  context: async (contactId: string): Promise<LeadContextResponse> => {
    const response = await axiosInstance.get<LeadContextResponse>(`${BASE_URL}/leads/${contactId}`);
    return response.data;
  },
  markAsLead: async (contactId: string): Promise<LeadContextResponse> => {
    const response = await axiosInstance.post<LeadContextResponse>(
      `${BASE_URL}/leads/${contactId}/mark-as-lead`,
    );
    return response.data;
  },
  unmarkAsLead: async (contactId: string): Promise<LeadContextResponse> => {
    const response = await axiosInstance.post<LeadContextResponse>(
      `${BASE_URL}/leads/${contactId}/unmark-as-lead`,
    );
    return response.data;
  },
  updateStatus: async (
    contactId: string,
    lead_status: string,
    completions?: CriterionCompletionUpdate[],
    lead_status_detail?: LeadStatusDetail | null,
  ) => {
    // `completions` is omitted rather than sent empty when there is nothing to
    // record: the route treats an absent list as "no ticks to write", and an
    // empty one would be an equally valid but pointless round trip through the
    // criterion-provenance check.
    //
    // `lead_status_detail` is the opposite case and must travel as an EXPLICIT
    // null when the picker's "no detail" arm was chosen: the column is being
    // rewritten by this move, so omitting the key would leave a rung's stale
    // detail attached to the new one.
    const response = await axiosInstance.put(`${BASE_URL}/leads/${contactId}/status`, {
      lead_status,
      lead_status_detail: lead_status_detail ?? null,
      ...(completions?.length ? { completions } : {}),
    });
    return response.data.data;
  },
  updateRoutedPipeline: async (contactId: string, routed_pipeline_id: string | null) => {
    const response = await axiosInstance.put(
      `${BASE_URL}/leads/${contactId}/routed-pipeline`,
      { routed_pipeline_id },
    );
    return response.data.data;
  },
  createDeal: async (contactId: string, data: LeadCreateDealData): Promise<Deal> => {
    const response = await axiosInstance.post(`${BASE_URL}/leads/${contactId}/create-deal`, data);
    return response.data.data;
  },
  statusCounts: async (params?: LeadListFilterParams) => {
    const response = await axiosInstance.get<LeadStatusCountsResponse>(
      `${BASE_URL}/leads/status-counts`,
      { params },
    );
    return response.data.data;
  },
  bulkStatus: async (
    scope: LeadBulkScope,
    leadStatus: string,
    leadStatusDetail?: LeadStatusDetail | null,
  ) => {
    const response = await axiosInstance.post<LeadBulkStatusResponse>(
      `${BASE_URL}/leads/bulk-status`,
      {
        ...bulkScopeBody(scope),
        lead_status: leadStatus,
        lead_status_detail: leadStatusDetail ?? null,
      },
    );
    return response.data;
  },
  /**
   * `ownerId` of `null` CLEARS the owner — "Unassigned" is a state the list renders
   * an Owner column and an Owner filter for, and this is the way back to it. Spelled
   * `?? null` rather than a falsy guard so the key is always on the wire: the server
   * takes `owner_id` as nullable-but-REQUIRED, so a dropped key is a 422, never a
   * silent clear.
   */
  bulkAssignOwner: async (scope: LeadBulkScope, ownerId: string | null) => {
    const response = await axiosInstance.post<LeadBulkAssignOwnerResponse>(
      `${BASE_URL}/leads/bulk-assign-owner`,
      { ...bulkScopeBody(scope), owner_id: ownerId ?? null },
    );
    return response.data;
  },
};

// Hooks
export function useLeads(filters: LeadFilters = {}) {
  // Memoize cleaned params to prevent unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => cleanParams(filters), [
    filters,
    filters.search,
    filters.lead_status,
    filters.lead_status_detail,
    filters.source,
    filters.owner_id,
    filters.organization_id,
    filters.created_from,
    filters.created_to,
    filters.sort_by,
    filters.sort_direction,
    filters.skip,
    filters.limit,
  ]);

  return useQuery({
    queryKey: leadKeys.list(stableParams),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => leadsApi.list(stableParams),
  });
}

/**
 * Per-milestone lead counts for the board's column badges.
 *
 * Takes the SAME filters the list sends, so a badge can never describe a
 * different set than the cards under it. Kept a separate query rather than
 * derived from the fetched page, because the board holds one bounded slice and
 * a count derived from it would silently under-report every busy column.
 */
export function useLeadStatusCounts(filters: LeadListFilterParams = {}) {
  const stableParams = useMemo(
    () => cleanParams(filters) as LeadListFilterParams | undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters,
      filters.search,
      filters.lead_status,
      filters.lead_status_detail,
      filters.source,
      filters.owner_id,
      filters.organization_id,
      filters.created_from,
      filters.created_to,
    ],
  );
  return useQuery({
    queryKey: leadKeys.statusCounts(stableParams),
    placeholderData: keepPreviousData,
    queryFn: () => leadsApi.statusCounts(stableParams),
  });
}

export function useBulkUpdateLeadStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      leadStatus,
      leadStatusDetail,
    }: {
      scope: LeadBulkScope;
      leadStatus: string;
      /** The second layer, chosen in the same picker the single-row chip opens. */
      leadStatusDetail?: LeadStatusDetail | null;
    }) => leadsApi.bulkStatus(scope, leadStatus, leadStatusDetail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkAssignLeadOwner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scope, ownerId }: { scope: LeadBulkScope; ownerId: string | null }) =>
      leadsApi.bulkAssignOwner(scope, ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LeadCreateData) => leadsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/**
 * One contact's lead funnel state — the seam the master-data contact page reads.
 *
 * `lead_status` and `routed_pipeline_id` live on `pipeline_contact_status`, which
 * the contact payload does not carry, so the contact page has no other way to see
 * where a lead sits in the funnel. `enabled` is the caller's `lead`-relationship
 * gate: pass `isLead` and the query never fires for a contact the route would
 * 400 anyway. (It used to mirror `useProspect` on the second, prospecting ladder;
 * that ladder merged into this one, so this is now the ONE funnel-state read.)
 */
export function useLeadContext(contactId: string, enabled = true) {
  return useQuery({
    queryKey: leadKeys.context(contactId),
    queryFn: () => leadsApi.context(contactId),
    enabled: enabled && !!contactId,
  });
}

/**
 * Put an existing contact on the lead rung and open its funnel state.
 *
 * `lead` is lifecycle-owned, so the contact detail page renders it LOCKED in the
 * classification control — this is the only way to promote a contact to a lead
 * from its own page. The rung goes through the backend's lifecycle seam, never a
 * rebind of `contact_relationship`.
 */
export function useMarkAsLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => leadsApi.markAsLead(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/**
 * Take a contact off the lead rung and close its funnel state.
 *
 * The mirror of `useMarkAsLead`, and the only per-record demote in the app: the
 * classification control renders `lead` LOCKED and `PUT /contacts/{id}` unions
 * the stored rungs back into any payload, so a demote cannot be expressed as a
 * contact edit. `POST /leads/{id}/unmark-as-lead` clears `lead_status` and
 * `routed_pipeline_id` and appends the funnel trail's exit row in the same
 * transaction as the rung removal.
 *
 * `lead-milestone-criteria` is invalidated alongside the two the promote twin
 * refreshes: the qualification ticks deliberately SURVIVE a demote, so a stale
 * cache would show them against a lead that is no longer on the ladder.
 *
 * **The lead-CONTEXT key is deliberately excluded from that invalidation.**
 * `LeadContextCard` gates its own query on `isLead`, read off the CACHED
 * `contact_relationship` — which still carries the rung for the render between
 * this mutation settling and the contacts refetch landing. So invalidating
 * `leadKeys.all` wholesale refetches a lead that no longer exists and logs a 404
 * on every single demote (the route answers 404 by design, and the frontend
 * suppresses it, so nothing breaks — it is pure noise on the one page most
 * likely to be open). Leaving the key stale is correct instead: the card
 * unmounts the moment the contact arrives without its rung, and re-promoting
 * remounts it against a key `useMarkAsLead` does invalidate.
 */
export function useUnmarkAsLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => leadsApi.unmarkAsLead(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: leadKeys.all,
        predicate: (query) => query.queryKey[1] !== 'context',
      });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['lead-milestone-criteria'] });
    },
  });
}

/**
 * Move a lead along the funnel.
 *
 * `completions` carries the qualification tick-boxes the gate dialog collected.
 * They travel WITH the move, in one request, so a refused gate rolls them back
 * rather than leaving a lead whose criteria read as satisfied on a rung it
 * never reached.
 */
export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      lead_status,
      lead_status_detail,
      completions,
    }: {
      contactId: string;
      lead_status: string;
      /**
       * The rung's second layer. Explicitly nullable rather than optional at the
       * transport: the move rewrites the column either way, so "no detail" has to
       * be sayable.
       */
      lead_status_detail?: LeadStatusDetail | null;
      completions?: CriterionCompletionUpdate[];
    }) => leadsApi.updateStatus(contactId, lead_status, completions, lead_status_detail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // The gate dialog reads the lead's tick state; the move just changed it.
      queryClient.invalidateQueries({ queryKey: ['lead-milestone-criteria'] });
    },
  });
}

export function useCreateDealFromLead() {
  const queryClient = useQueryClient();
  const { rememberPipeline } = useLastUsedPipeline();
  return useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: LeadCreateDealData }) =>
      leadsApi.createDeal(contactId, data),
    onSuccess: (result, { data }) => {
      // Remember the chosen pipeline so the next New Deal pre-selects it. The
      // request may omit it (the backend then resolves the lead's routing), in
      // which case remember the one the deal actually landed in.
      const landedIn = data.pipeline_id ?? result.pipeline_id;
      if (landedIn) rememberPipeline(landedIn);
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
    },
  });
}

/**
 * Set (or clear) the pipeline a lead is routed to.
 *
 * The human half of the questionnaire routing (lead-attribution design §C/§D):
 * lead capture stamps `routed_pipeline_id` from the connection's per-form answer
 * rules, and this is the override. It stays an INTENT — no deal is created.
 */
export function useUpdateLeadRoutedPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      routed_pipeline_id,
    }: {
      contactId: string;
      routed_pipeline_id: string | null;
    }) => leadsApi.updateRoutedPipeline(contactId, routed_pipeline_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}
